import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Stack,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { Reply as ReplyIcon } from '@mui/icons-material';
import { feedbackApi, type Feedback } from '../services/api';
import PageHeader from '../components/PageHeader';

const statusMap: Record<string, { label: string; color: 'warning' | 'info' | 'success' | 'default' | 'error' }> = {
  PENDING: { label: '待处理', color: 'warning' },
  PROCESSING: { label: '处理中', color: 'info' },
  RESOLVED: { label: '已解决', color: 'success' },
  CLOSED: { label: '已关闭', color: 'default' },
};

const categoryMap: Record<string, string> = {
  BUG: '缺陷',
  FEATURE: '功能建议',
  USAGE: '使用咨询',
  OTHER: '其他',
};

const CustomerSuccess: React.FC = () => {
  const [items, setItems] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [selected, setSelected] = useState<Feedback | null>(null);
  const [status, setStatus] = useState<Feedback['status']>('PENDING');
  const [reply, setReply] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await feedbackApi.list();
      setItems(data);
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.message || '加载反馈列表失败', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openReply = (item: Feedback) => {
    setSelected(item);
    setStatus(item.status);
    setReply(item.reply || '');
  };

  const handleSave = async () => {
    if (!selected) return;
    try {
      await feedbackApi.updateStatus(selected.id, status, reply);
      setSnackbar({ open: true, message: '反馈状态更新成功', severity: 'success' });
      setSelected(null);
      load();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.message || '更新失败', severity: 'error' });
    }
  };

  return (
    <Box>
      <PageHeader title="客户成功" subtitle="收集并处理客户反馈，跟踪使用健康度" />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">总反馈</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{items.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">待处理</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {items.filter((i) => i.status === 'PENDING').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">已解决</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {items.filter((i) => i.status === 'RESOLVED').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">处理率</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {items.length === 0 ? '0%' : Math.round(
                  (items.filter((i) => i.status === 'RESOLVED' || i.status === 'CLOSED').length / items.length) * 100
                ) + '%'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>分类</TableCell>
              <TableCell>标题</TableCell>
              <TableCell>内容</TableCell>
              <TableCell>联系人</TableCell>
              <TableCell>提交人</TableCell>
              <TableCell>状态</TableCell>
              <TableCell>处理人</TableCell>
              <TableCell>创建时间</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {loading ? '加载中...' : '暂无客户反馈'}
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>{categoryMap[item.category] || item.category}</TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.content}
                  </TableCell>
                  <TableCell>{item.contact || '-'}</TableCell>
                  <TableCell>{item.username || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={statusMap[item.status]?.label || item.status}
                      color={statusMap[item.status]?.color || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{item.handler || '-'}</TableCell>
                  <TableCell>{new Date(item.createdAt).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" startIcon={<ReplyIcon />} onClick={() => openReply(item)}>
                      处理
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>处理反馈 #{selected?.id}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
            {selected?.title}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {selected?.content}
          </Typography>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>状态</InputLabel>
              <Select value={status} label="状态" onChange={(e) => setStatus(e.target.value as Feedback['status'])}>
                <MenuItem value="PENDING">待处理</MenuItem>
                <MenuItem value="PROCESSING">处理中</MenuItem>
                <MenuItem value="RESOLVED">已解决</MenuItem>
                <MenuItem value="CLOSED">已关闭</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="回复内容"
              multiline
              rows={4}
              fullWidth
              value={reply}
              onChange={(e) => setReply(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>取消</Button>
          <Button variant="contained" onClick={handleSave}>保存</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerSuccess;
